import { FunctionComponent, useContext } from 'react'
import styled, { css } from 'styled-components'
import contextToiTeaser from '@contexts/toiTeaser'
import { TOITeaserLayoutContextType } from '@contexts/toiTeaser'
import { TitleProps } from '@widgets/Teaser/toi/types'
import { adjustSizeIfRomandie } from '@widgets/Teaser/toi/utils'
import { computeTeaserWidth } from '@widgets/Teaser/utils'
import { ViewportTypeValues } from '@contexts/viewport'
import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'

type StyledTitleProps = Pick<
  TOITeaserLayoutContextType,
  'teaserFlex' | 'articleType'
>

const { TOITeaserLayoutContext } = contextToiTeaser

const StyledTitle = styled.div<StyledTitleProps>`
  ${({
    theme: {
      fonts: { goodOT: goodOTFontName, goodOTCond: goodCondFontName },
      colors: {
        white,
        grey: { grey_100 },
      },
    },
    teaserFlex,
    articleType,
  }) => {
    let fontSizeDesktop
    let textDecorationThicknessDesktop
    const desktopTeaserFlex = teaserFlex['desktop']
    const teaserWidthDesktop = computeTeaserWidth({
      flexValue: desktopTeaserFlex,
      viewportType: ViewportTypeValues.DESKTOP,
    })

    if (desktopTeaserFlex === '6') {
      fontSizeDesktop = `${
        adjustSizeIfRomandie(0.07936) * teaserWidthDesktop
      }px`
      textDecorationThicknessDesktop = '7px'
    } else if (desktopTeaserFlex === '4') {
      fontSizeDesktop = `${
        adjustSizeIfRomandie(0.12048) * teaserWidthDesktop
      }px`
      textDecorationThicknessDesktop = '6px'
    } else if (desktopTeaserFlex === '3') {
      fontSizeDesktop = `${
        adjustSizeIfRomandie(0.12195) * teaserWidthDesktop
      }px`
      textDecorationThicknessDesktop = '5px'
    } else if (desktopTeaserFlex === '2') {
      fontSizeDesktop = `${
        adjustSizeIfRomandie(0.11875) * teaserWidthDesktop
      }px`
      textDecorationThicknessDesktop = '4px'
    }

    let fontSizeTablet
    let textDecorationThicknessTablet
    const tabletTeaserFlex = teaserFlex['tablet']
    const teaserWidthTablet = computeTeaserWidth({
      flexValue: tabletTeaserFlex,
      viewportType: ViewportTypeValues.TABLET,
    })

    if (tabletTeaserFlex === '6') {
      fontSizeTablet = `${adjustSizeIfRomandie(0.12198) * teaserWidthTablet}px`
      textDecorationThicknessTablet = '6px'
    } else if (tabletTeaserFlex === '3') {
      fontSizeTablet = `${adjustSizeIfRomandie(0.11645) * teaserWidthTablet}px`
      textDecorationThicknessTablet = '4px'
    }

    const textDecorationThicknessMobile = '4px'

    const textDecorationStyleDesktop = css`
      text-decoration-line: underline;
      text-decoration-thickness: ${textDecorationThicknessDesktop};
      text-decoration-color: ${white};
    `

    const textDecorationStyleTablet = css`
      text-decoration-line: underline;
      text-decoration-thickness: ${textDecorationThicknessTablet};
      text-decoration-color: ${white};
    `

    const textDecorationStyleMobile = css`
      text-decoration-line: underline;
      text-decoration-thickness: ${textDecorationThicknessMobile};
      text-decoration-color: ${white};
    `

    return css`
      font-family: ${articleType !== 'default'
        ? goodOTFontName
        : goodCondFontName};
      font-style: ${articleType !== 'default' ? 'italic' : 'normal'};
      font-weight: 900;
      line-height: 1;
      ${articleType === 'default' &&
      css`
        text-shadow: 0 0 8px ${grey_100}, 0 0 8px ${grey_100};
      `}
      color: ${articleType === 'column' ? grey_100 : white};
      > strong {
        color: ${articleType === 'column' ? grey_100 : white};
        font-weight: 900;
        font-style: ${articleType !== 'default' ? 'italic' : 'normal'};
      }
      > p {
        margin: 0;
        font-style: ${articleType !== 'default' ? 'italic' : 'normal'};
      }

      ${desktopCSS(css`
        font-size: ${fontSizeDesktop};
        ${articleType === 'commentary' && textDecorationStyleDesktop}
        > strong {
          ${articleType === 'commentary' && textDecorationStyleDesktop}
        }
        > p {
          ${articleType === 'commentary' && textDecorationStyleDesktop}
        }
      `)}

      ${tabletCSS(css`
        font-size: ${fontSizeTablet};
        ${articleType === 'commentary' && textDecorationStyleTablet}
        > strong {
          ${articleType === 'commentary' && textDecorationStyleTablet}
        }
        > p {
          ${articleType === 'commentary' && textDecorationStyleTablet}
        }
      `)}

      ${mobileCSS(css`
        font-size: ${adjustSizeIfRomandie(11.8)}vw;
        ${articleType === 'commentary' && textDecorationStyleMobile} > strong {
          ${articleType === 'commentary' && textDecorationStyleMobile}
        }
        > p {
          ${articleType === 'commentary' && textDecorationStyleMobile}
        }
      `)}
    `
  }}
`
const Title: FunctionComponent<TitleProps> = ({ children }) => {
  const { teaserFlex, articleType } = useContext(TOITeaserLayoutContext)

  return (
    <StyledTitle
      teaserFlex={teaserFlex}
      articleType={articleType}
      dangerouslySetInnerHTML={{
        __html: children,
      }}
    />
  )
}

export default Title
